export class EffectsSynth {
  private ctx: AudioContext;
  private output: GainNode;

  constructor(ctx: AudioContext, output: GainNode) {
    this.ctx = ctx;
    this.output = output;
  }

  playStart(): void {
    const now = this.ctx.currentTime;

    // Bright ascending tone
    const osc = this.ctx.createOscillator();
    const gain = this.ctx.createGain();

    osc.type = 'sine';
    osc.frequency.setValueAtTime(523.25, now); // C5
    osc.frequency.exponentialRampToValueAtTime(1046.5, now + 0.15); // C6

    osc.connect(gain);
    gain.connect(this.output);

    gain.gain.setValueAtTime(0, now);
    gain.gain.linearRampToValueAtTime(0.3, now + 0.02);
    gain.gain.exponentialRampToValueAtTime(0.01, now + 0.3);

    osc.start(now);
    osc.stop(now + 0.35);
  }

  playSuccess(): void {
    const now = this.ctx.currentTime;

    // Major chord flourish (C major: C5, E5, G5)
    const frequencies = [523.25, 659.25, 783.99];
    const delays = [0, 0.05, 0.1];

    frequencies.forEach((freq, index) => {
      const osc = this.ctx.createOscillator();
      const gain = this.ctx.createGain();
      const delay = delays[index]!;

      osc.type = 'sine';
      osc.frequency.setValueAtTime(freq, now + delay);

      osc.connect(gain);
      gain.connect(this.output);

      const startTime = now + delay;
      gain.gain.setValueAtTime(0, startTime);
      gain.gain.linearRampToValueAtTime(0.25, startTime + 0.02);
      gain.gain.setValueAtTime(0.25, startTime + 0.3);
      gain.gain.exponentialRampToValueAtTime(0.01, startTime + 0.8);

      osc.start(startTime);
      osc.stop(startTime + 0.9);
    });

    // Add shimmer effect
    const shimmer = this.ctx.createOscillator();
    const shimmerGain = this.ctx.createGain();

    shimmer.type = 'triangle';
    shimmer.frequency.setValueAtTime(1046.5, now + 0.15); // C6
    shimmer.frequency.exponentialRampToValueAtTime(2093, now + 0.5); // C7

    shimmer.connect(shimmerGain);
    shimmerGain.connect(this.output);

    shimmerGain.gain.setValueAtTime(0, now + 0.15);
    shimmerGain.gain.linearRampToValueAtTime(0.1, now + 0.2);
    shimmerGain.gain.exponentialRampToValueAtTime(0.01, now + 0.6);

    shimmer.start(now + 0.15);
    shimmer.stop(now + 0.7);
  }

  playFailure(): void {
    const now = this.ctx.currentTime;

    // Descending minor chord (C minor: C4, Eb4, G4)
    const frequencies = [261.63, 311.13, 392];

    frequencies.forEach((freq) => {
      const osc = this.ctx.createOscillator();
      const gain = this.ctx.createGain();

      osc.type = 'sawtooth';
      // Descend slightly
      osc.frequency.setValueAtTime(freq, now);
      osc.frequency.exponentialRampToValueAtTime(freq * 0.9, now + 0.4);

      osc.connect(gain);
      gain.connect(this.output);

      gain.gain.setValueAtTime(0, now);
      gain.gain.linearRampToValueAtTime(0.15, now + 0.02);
      gain.gain.setValueAtTime(0.15, now + 0.2);
      gain.gain.exponentialRampToValueAtTime(0.01, now + 0.5);

      osc.start(now);
      osc.stop(now + 0.6);
    });

    // Low rumble
    const rumble = this.ctx.createOscillator();
    const rumbleGain = this.ctx.createGain();
    const rumbleFilter = this.ctx.createBiquadFilter();

    rumble.type = 'sawtooth';
    rumble.frequency.setValueAtTime(55, now);
    rumble.frequency.exponentialRampToValueAtTime(40, now + 0.5);

    rumbleFilter.type = 'lowpass';
    rumbleFilter.frequency.setValueAtTime(200, now);

    rumble.connect(rumbleFilter);
    rumbleFilter.connect(rumbleGain);
    rumbleGain.connect(this.output);

    rumbleGain.gain.setValueAtTime(0, now);
    rumbleGain.gain.linearRampToValueAtTime(0.3, now + 0.05);
    rumbleGain.gain.setValueAtTime(0.3, now + 0.2);
    rumbleGain.gain.exponentialRampToValueAtTime(0.01, now + 0.6);

    rumble.start(now);
    rumble.stop(now + 0.7);
  }

  playPersonalBest(): void {
    const now = this.ctx.currentTime;

    // Quick ascending arpeggio (C5 → E5 → G5 → C6)
    const frequencies = [523.25, 659.25, 783.99, 1046.5];
    const noteLength = 0.12;

    frequencies.forEach((freq, index) => {
      const osc = this.ctx.createOscillator();
      const gain = this.ctx.createGain();
      const startTime = now + index * noteLength;

      osc.type = 'sine';
      osc.frequency.setValueAtTime(freq, startTime);

      osc.connect(gain);
      gain.connect(this.output);

      gain.gain.setValueAtTime(0, startTime);
      gain.gain.linearRampToValueAtTime(0.3, startTime + 0.02);
      gain.gain.setValueAtTime(0.3, startTime + noteLength * 0.6);
      gain.gain.exponentialRampToValueAtTime(0.01, startTime + noteLength * 1.5);

      osc.start(startTime);
      osc.stop(startTime + noteLength * 1.6);
    });

    // Add sparkle on top note
    const sparkle = this.ctx.createOscillator();
    const sparkleGain = this.ctx.createGain();

    sparkle.type = 'triangle';
    sparkle.frequency.setValueAtTime(2093, now + frequencies.length * noteLength);

    sparkle.connect(sparkleGain);
    sparkleGain.connect(this.output);

    const sparkleStart = now + frequencies.length * noteLength;
    sparkleGain.gain.setValueAtTime(0, sparkleStart);
    sparkleGain.gain.linearRampToValueAtTime(0.15, sparkleStart + 0.02);
    sparkleGain.gain.exponentialRampToValueAtTime(0.01, sparkleStart + 0.4);

    sparkle.start(sparkleStart);
    sparkle.stop(sparkleStart + 0.5);
  }

  playHighScore(): void {
    const now = this.ctx.currentTime;

    // Victory fanfare - brass-like rising progression
    // First chord: C major (C4, E4, G4)
    const chord1 = [261.63, 329.63, 392];
    // Second chord: F major (F4, A4, C5)
    const chord2 = [349.23, 440, 523.25];
    // Final chord: G major -> C major resolution (G4, B4, D5 -> C5, E5, G5)
    const chord3 = [392, 493.88, 587.33];
    const finalChord = [523.25, 659.25, 783.99];

    // Play chord progression
    const playChord = (frequencies: number[], startTime: number, duration: number, volume: number) => {
      frequencies.forEach((freq) => {
        const osc = this.ctx.createOscillator();
        const gain = this.ctx.createGain();

        osc.type = 'sawtooth';
        osc.frequency.setValueAtTime(freq, startTime);

        // Add slight filter for brass-like tone
        const filter = this.ctx.createBiquadFilter();
        filter.type = 'lowpass';
        filter.frequency.setValueAtTime(2000, startTime);

        osc.connect(filter);
        filter.connect(gain);
        gain.connect(this.output);

        gain.gain.setValueAtTime(0, startTime);
        gain.gain.linearRampToValueAtTime(volume, startTime + 0.03);
        gain.gain.setValueAtTime(volume, startTime + duration * 0.7);
        gain.gain.exponentialRampToValueAtTime(0.01, startTime + duration);

        osc.start(startTime);
        osc.stop(startTime + duration + 0.1);
      });
    };

    // Chord progression timing
    playChord(chord1, now, 0.3, 0.15);
    playChord(chord2, now + 0.3, 0.3, 0.18);
    playChord(chord3, now + 0.6, 0.25, 0.2);
    playChord(finalChord, now + 0.85, 0.8, 0.25);

    // Shimmer tail on final chord
    const shimmerFreqs = [2093, 2637, 3136]; // C7, E7, G7
    shimmerFreqs.forEach((freq, index) => {
      const osc = this.ctx.createOscillator();
      const gain = this.ctx.createGain();
      const startTime = now + 1.0 + index * 0.05;

      osc.type = 'sine';
      osc.frequency.setValueAtTime(freq, startTime);

      osc.connect(gain);
      gain.connect(this.output);

      gain.gain.setValueAtTime(0, startTime);
      gain.gain.linearRampToValueAtTime(0.08, startTime + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.01, startTime + 0.8);

      osc.start(startTime);
      osc.stop(startTime + 0.9);
    });
  }

  playRank3(): void {
    const now = this.ctx.currentTime;

    // Simple two-note bronze chime (G4 → C5)
    const frequencies = [392, 523.25];
    const noteLength = 0.15;

    frequencies.forEach((freq, index) => {
      const osc = this.ctx.createOscillator();
      const gain = this.ctx.createGain();
      const startTime = now + index * noteLength;

      osc.type = 'triangle';
      osc.frequency.setValueAtTime(freq, startTime);

      osc.connect(gain);
      gain.connect(this.output);

      gain.gain.setValueAtTime(0, startTime);
      gain.gain.linearRampToValueAtTime(0.25, startTime + 0.02);
      gain.gain.setValueAtTime(0.25, startTime + noteLength * 0.5);
      gain.gain.exponentialRampToValueAtTime(0.01, startTime + noteLength * 1.5);

      osc.start(startTime);
      osc.stop(startTime + noteLength * 1.6);
    });
  }
}
